import React, { useRef, useEffect, useMemo, useState } from 'react';
import { useVoice } from '../contexts/VoiceContext';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { MonitorUp, MicOff, Video } from 'lucide-react';

const VideoTile = ({ stream, label, avatar, isSelf, muted: isMuted, type, isActive, onToggle, ownerId, isSpeaking }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  const showVideo = stream && (type !== 'screen' || isActive);
  const isScreen = type === 'screen';

  return (
    <div
      className={`relative bg-[#12141a] rounded-lg overflow-hidden border border-white/10 aspect-video ${isScreen ? 'cursor-pointer' : ''} ${isSpeaking ? 'ring-2 ring-green-400 shadow-[0_0_20px_rgba(34,197,94,0.35)]' : ''}`}
      data-testid={`voice-tile-${ownerId}-${type}`}
      onClick={() => isScreen && onToggle?.()}
    >
      {showVideo ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-3">
          {isScreen ? (
            <div className="flex flex-col items-center gap-2 text-zinc-400">
              <MonitorUp className="h-8 w-8" />
              <span className="text-xs">Partajare ecran</span>
              <span className="text-[11px]">Apasă pentru a vedea</span>
            </div>
          ) : (
            <Avatar className="h-16 w-16">
              <AvatarImage src={avatar} />
              <AvatarFallback className="bg-cyan-600/20 text-cyan-300 text-2xl">
                {label?.[0]?.toUpperCase() || '?'}
              </AvatarFallback>
            </Avatar>
          )}
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gradient-to-t from-black/70 to-transparent">
        <div className="flex items-center gap-1.5">
          {isScreen ? <Video className="h-3 w-3 text-cyan-300" /> : null}
          <span className="text-xs text-white font-medium truncate">{label}</span>
          {isSpeaking && <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse" data-testid={`voice-speaking-${ownerId}`} />}
          {isMuted && <MicOff className="h-3 w-3 text-red-400 flex-shrink-0" />}
        </div>
      </div>
    </div>
  );
};

export const VideoGrid = () => {
  const { voiceChannel, localVideoStream, remoteStreams, cameraOn, screenSharing, muted, voiceUsers, speaking } = useVoice();
  const { user } = useAuth();
  const [activeScreenId, setActiveScreenId] = useState(null);

  const combinedUsers = useMemo(() => {
    const list = voiceUsers.map(v => {
      if (v.user_id === user?.user_id) {
        return {
          ...v,
          user: user || v.user,
          muted: muted,
          camera: cameraOn,
          screen: screenSharing,
        };
      }
      return v;
    });
    if (user && !list.find(v => v.user_id === user.user_id)) {
      list.push({ user_id: user.user_id, user, muted, camera: cameraOn, screen: screenSharing });
    }
    return list;
  }, [voiceUsers, user, muted, cameraOn, screenSharing]);

  const screenOwners = useMemo(() => combinedUsers.filter(v => v.screen).map(v => v.user_id), [combinedUsers]);

  useEffect(() => {
    if (screenOwners.length === 0) {
      setActiveScreenId(null);
      return;
    }
    if (!screenOwners.includes(activeScreenId)) {
      if (screenSharing && user?.user_id) {
        setActiveScreenId(user.user_id);
      } else {
        setActiveScreenId(screenOwners[0]);
      }
    }
  }, [screenOwners, activeScreenId, screenSharing, user]);

  if (!voiceChannel) return null;

  const tiles = [];

  combinedUsers.forEach(vu => {
    const isSelf = vu.user_id === user?.user_id;
    const cameraStream = isSelf ? (cameraOn ? localVideoStream : null) : remoteStreams[vu.user_id]?.camera;
    const screenStream = isSelf ? (screenSharing ? localVideoStream : null) : remoteStreams[vu.user_id]?.screen;
    const hasScreen = Boolean(vu.screen);
    const hasCamera = Boolean(vu.camera);
    const displayName = vu.user?.display_name || vu.user?.username || 'User';
    const isSpeaking = Boolean(speaking?.[vu.user_id]);

    if (hasScreen) {
      tiles.push({
        key: `screen-${vu.user_id}`,
        ownerId: vu.user_id,
        stream: screenStream,
        label: `${displayName} • Ecran`,
        avatar: vu.user?.avatar,
        isSelf,
        muted: vu.muted || false,
        type: 'screen',
        isSpeaking,
      });
    }

    const userStream = hasCamera ? cameraStream : null;
    tiles.push({
      key: `user-${vu.user_id}`,
      ownerId: vu.user_id,
      stream: userStream,
      label: displayName,
      avatar: vu.user?.avatar,
      isSelf,
      muted: vu.muted || false,
      type: 'user',
      isSpeaking,
    });
  });

  if (tiles.length === 0) return null;

  const gridCols = tiles.length === 1 ? 'grid-cols-1' : tiles.length <= 4 ? 'grid-cols-2' : 'grid-cols-3';

  return (
    <div className="w-full" data-testid="voice-video-grid">
      <div className={`grid ${gridCols} gap-4 max-w-5xl mx-auto`} data-testid="voice-video-grid-inner">
        {tiles.map(tile => (
          <VideoTile
            key={tile.key}
            stream={tile.stream}
            label={tile.label}
            avatar={tile.avatar}
            isSelf={tile.isSelf}
            muted={tile.muted}
            type={tile.type}
            ownerId={tile.ownerId}
            isSpeaking={tile.isSpeaking}
            isActive={tile.type === 'screen' ? activeScreenId === tile.ownerId : true}
            onToggle={() => {
              if (tile.type === 'screen') {
                setActiveScreenId(prev => (prev === tile.ownerId ? null : tile.ownerId));
              }
            }}
          />
        ))}
      </div>
    </div>
  );
};
